import { Fragment, useMemo, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { deriveFlightLogPhase, type FlightLogPhase } from '@@api/flightLog.shared'
import { AppLayout } from '@/components/AppLayout'
import { TopBarBackButton } from '@/components/topbar'
import { DesktopPage } from '@/components/desktop/DesktopPage'
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader'
import { useIsDesktop } from '@/components/ui/use-mobile'
import { Card } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { DetailSkeleton } from '@/components/ui/skeleton-presets'
import { DateInput } from '@/components/DateInput'
import { PrntrCard, PrntrInput, PrntrSelect } from '@/components/ui/prntr-input'
import { EmptyState } from '@/components/EmptyState'
import { ArrowRight, Calendar, Plane, PlaneTakeoff } from '@/components/icons'
import { FlightDrawer } from '@/components/FlightDrawer'
import { FlightLogDrawer } from '@/components/FlightLogDrawer'
import { Pill } from '@/components/ui/pill'
import { useAircraftGetAll } from '@/lib/hooks'
import { useFlightLogGetCompanyLegs, type CompanyFlightLogLeg } from '@/lib/hooks/flightLog'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'
import { useIsPilot } from '@/hooks/useIsPilot'
import { hapticLight } from '@/lib/haptics'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_auth/flights/')({
  component: CompanyFlightLogsRoute,
})

const ALL_AIRCRAFT = 'all'

// Flight status dot, read with the row's own status text (no legend):
//   green  — flown and the flight log is submitted
//   yellow — flown, flight log still to be submitted
//   blue   — not flown yet, no flight log action needed
// The --positive / --warning / --info aliases from index.css, so light/dark
// themes both work.
const phaseDot: Record<FlightLogPhase, string> = {
  complete: 'bg-(--positive)',
  pending: 'bg-(--warning)',
  upcoming: 'bg-(--info)',
}

type LegRow = CompanyFlightLogLeg & { phase: FlightLogPhase }

// Lists are keyed on the flight day (UTC), matching every other flight-log list.
function legDate(leg: CompanyFlightLogLeg) {
  return dayjs.utc(leg.departureDateTime)
}

function CompanyFlightLogsRoute() {
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const { isAdmin, isLoaded: isAdminLoaded } = useIsAdmin()
  const { isPilot, isLoaded: isPilotLoaded } = useIsPilot()
  const canView = isAdmin || isPilot
  const roleLoaded = isAdminLoaded && isPilotLoaded

  // Start date drives the list (Leon-style): every leg from that day onwards,
  // capped at a year so upcoming flights appear and the server window holds.
  const [startDate, setStartDate] = useState<Date>(() => dayjs().startOf('month').toDate())
  const range = useMemo(() => {
    const from = dayjs.utc(dayjs(startDate).format('YYYY-MM-DD'))
    return { from: from.toDate(), to: from.add(12, 'month').toDate() }
  }, [startDate])

  const [aircraftId, setAircraftId] = useState<string>(ALL_AIRCRAFT)

  const { data: aircraftData } = useAircraftGetAll()
  const aircraft = (aircraftData ?? []) as { id: string; registration: string }[]
  const { data: legs, isLoading } = useFlightLogGetCompanyLegs(range, canView)

  // Leg sticks around after close so the drawers can animate out
  const [selectedLeg, setSelectedLeg] = useState<LegRow | null>(null)
  const [isLogOpen, setIsLogOpen] = useState(false)
  const [isFlightOpen, setIsFlightOpen] = useState(false)

  // One clock per render so every row agrees on "now"
  const now = new Date()
  const allRows: LegRow[] = ((legs ?? []) as CompanyFlightLogLeg[]).map((leg) => ({
    ...leg,
    phase: deriveFlightLogPhase(leg, now),
  }))
  const rows =
    aircraftId === ALL_AIRCRAFT ? allRows : allRows.filter((leg) => leg.aircraftId === aircraftId)

  const openTrip = (leg: LegRow) => {
    const tripId = leg.tripId
    const search = { legId: leg.tripLegId }
    if (isAdmin) {
      navigate({ to: '/operations/trips/$tripId', params: { tripId }, search } as any)
    } else {
      navigate({ to: '/schedule/$tripId', params: { tripId }, search } as any)
    }
  }

  // Rows are not tappable; each carries explicit Flight / Log links.
  const openFlight = (leg: LegRow) => {
    hapticLight()
    setSelectedLeg(leg)
    setIsFlightOpen(true)
  }
  // Future legs have no loggable window yet (a save would be rejected), so
  // the Log link is only offered once the leg has departed.
  const openLog = (leg: LegRow) => {
    hapticLight()
    setSelectedLeg(leg)
    setIsLogOpen(true)
  }
  const links = { onFlight: openFlight, onLog: openLog }

  const aircraftOptions = [
    { value: ALL_AIRCRAFT, label: 'All aircraft' },
    ...aircraft.map((a) => ({ value: a.id, label: a.registration })),
  ]

  // PRNTR rows (the design's drawer-row inputs) inside a bordered card. The
  // date row mirrors DateObjectRow's bare styling; the aircraft row is the
  // pop-up select so iOS shows its native picker.
  const filters = (
    <PrntrCard className={isDesktop ? 'w-96' : undefined}>
      <PrntrInput label="From" iconLeft={<Calendar />}>
        <DateInput
          value={startDate}
          onChange={(date) => date && setStartDate(date)}
          title="Start date"
          clearable={false}
          className={cn(
            'h-auto w-auto justify-end border-0 bg-transparent p-0 shadow-none',
            'focus-within:ring-0 focus-within:border-transparent',
            '[&>div:first-child]:hidden [&_input]:flex-none [&_input]:w-28 [&_input]:text-right [&_input]:font-semibold',
            '[&_input::-webkit-date-and-time-value]:text-right'
          )}
        />
      </PrntrInput>
      <PrntrSelect
        label="Aircraft"
        iconLeft={<Plane />}
        value={aircraftId}
        onChange={setAircraftId}
        options={aircraftOptions}
      />
    </PrntrCard>
  )

  const emptyState = !rows.length ? (
    <EmptyState
      icon={PlaneTakeoff}
      title={allRows.length ? 'No flights for this aircraft' : `No flights from ${dayjs(startDate).format('D MMM YYYY')}`}
      description={
        allRows.length
          ? 'Try a different aircraft.'
          : 'Flights departing on or after the start date will appear here.'
      }
    />
  ) : null

  const body = (
    <div className="flex flex-col gap-4">
      {filters}

      {!roleLoaded || isLoading ? (
        <DetailSkeleton rows={5} />
      ) : !canView ? (
        <EmptyState
          icon={PlaneTakeoff}
          title="Pilots only"
          description="Flight logs are available to pilots and admins."
        />
      ) : (
        (emptyState ??
          (isDesktop ? (
            <FlightLogsTable rows={rows} {...links} />
          ) : (
            <FlightLogsList rows={rows} {...links} />
          )))
      )}

      {selectedLeg && (
        <FlightDrawer
          isOpen={isFlightOpen}
          onClose={() => setIsFlightOpen(false)}
          tripId={selectedLeg.tripId}
          tripLegId={selectedLeg.tripLegId}
          mode="edit"
          readOnly={!isAdmin}
        />
      )}

      {selectedLeg && (
        <FlightLogDrawer
          isOpen={isLogOpen}
          onClose={() => setIsLogOpen(false)}
          tripLegId={selectedLeg.tripLegId}
          readOnly={!isPilot || selectedLeg.lifecycle === 'complete'}
          initialSection={selectedLeg.defectDeferred ? 'defects' : undefined}
          onViewTrip={() => openTrip(selectedLeg)}
        />
      )}
    </div>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title="Flights"
          breadcrumb={
            <span>
              <Link to="/more" className="hover:underline">
                More
              </Link>
              <span className="text-content-primary-subtle"> / Flights</span>
            </span>
          }
        />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout topBar={<TopBarBackButton title="Flights" />} className="py-4">
      {body}
    </AppLayout>
  )
}

// Colour-coded circle for a phase, shown at the start of each flight cell
function PhaseDot({ phase, className }: { phase: FlightLogPhase; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('inline-block size-2.5 shrink-0 rounded-full', phaseDot[phase], className)}
    />
  )
}

// DEP → ARR with the phase dot, shared by the table cell and the mobile row
function FlightCell({ leg, className }: { leg: LegRow; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-sm font-semibold text-content-primary-bold',
        className
      )}
    >
      <PhaseDot phase={leg.phase} className="mr-0.5" />
      <span>{leg.departureAirportIcao}</span>
      <ArrowRight className="size-3 text-content-primary-subtle-mid" />
      <span>{leg.arrivalAirportIcao}</span>
      {/* The row's "Log #n" link carries the number, so the chip only flags state */}
      {leg.phase === 'pending' && leg.logNumber !== null && <Pill variant="draft">Draft</Pill>}
      {leg.defectDeferred && (
        <span className="rounded-full bg-background-negative-subtle px-2 py-0.5 text-xs font-semibold text-content-negative-bold">
          Defect Deferred
        </span>
      )}
    </span>
  )
}

type RowLinks = {
  onFlight: (leg: LegRow) => void
  onLog: (leg: LegRow) => void
}

// Explicit links per row: the flight (FlightDrawer) and, once the leg has
// departed, its technical log.
function RowLinkButtons({ leg, onFlight, onLog }: { leg: LegRow } & RowLinks) {
  return (
    <span className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => onFlight(leg)}
        className="text-sm font-semibold text-content-brand-bold hover:underline"
      >
        Flight
      </button>
      {leg.phase !== 'upcoming' && (
        <button
          type="button"
          onClick={() => onLog(leg)}
          className="whitespace-nowrap text-sm font-semibold text-content-brand-bold hover:underline"
        >
          {/* "Log #7" once the log exists; a departed leg with no log yet is just "Log" */}
          Log{leg.logNumber !== null && ` #${leg.logNumber}`}
        </button>
      )}
    </span>
  )
}

function FlightLogsTable({ rows, onFlight, onLog }: { rows: LegRow[] } & RowLinks) {
  return (
    <Card className="p-0 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-surface-L1 hover:bg-surface-L1">
            <TableHead className="px-4">Aircraft</TableHead>
            <TableHead>Flight</TableHead>
            <TableHead>Trip</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Signed off by</TableHead>
            <TableHead className="px-4">Links</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((leg) => (
            <TableRow key={leg.id}>
              <TableCell className="px-4 font-medium text-content-primary-bold">
                {leg.aircraftRegistration ?? '–'}
              </TableCell>
              <TableCell>
                <FlightCell leg={leg} />
              </TableCell>
              <TableCell className="max-w-56 truncate text-content-primary-bold">
                {leg.tripName}
              </TableCell>
              <TableCell className="text-content-primary-subtle">
                {legDate(leg).format('D MMM YYYY')}
              </TableCell>
              <TableCell className="text-content-primary-subtle">
                {leg.signedOffBy ?? '–'}
              </TableCell>
              <TableCell className="px-4">
                <RowLinkButtons leg={leg} onFlight={onFlight} onLog={onLog} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  )
}

function FlightLogsList({ rows, onFlight, onLog }: { rows: LegRow[] } & RowLinks) {
  // Month buckets; server order is ascending so insertion order stays
  // chronological and keying by label means a month can never repeat.
  const monthMap = new Map<string, LegRow[]>()
  for (const leg of rows) {
    const label = legDate(leg).format('MMM YYYY')
    const group = monthMap.get(label)
    if (group) {
      group.push(leg)
    } else {
      monthMap.set(label, [leg])
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {Array.from(monthMap, ([label, legs]) => (
        <Fragment key={label}>
          <div className="flex justify-center">
            <span className="rounded-full bg-surface-L2 px-3 py-1 text-xs font-semibold text-content-primary-subtle">
              {label}
            </span>
          </div>
          <Card className="flex flex-col gap-0 p-0 overflow-hidden">
            {legs.map((leg, idx) => (
              <Fragment key={leg.id}>
                <div className="flex flex-col gap-1.5 px-4 py-3 bg-surface-L0">
                  <div className="flex items-center justify-between gap-2">
                    <FlightCell leg={leg} />
                    <span className="shrink-0 text-sm font-semibold text-content-primary-bold">
                      {leg.aircraftRegistration ?? '–'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p className="min-w-0 truncate text-xs font-semibold leading-4 text-content-primary-subtle">
                      {leg.tripName} · {legDate(leg).format('D MMM, HH:mm')} ·{' '}
                      {leg.phase === 'complete'
                        ? leg.signedOffBy
                          ? `Signed off · ${leg.signedOffBy}`
                          : 'Signed off'
                        : leg.phase === 'pending'
                          ? 'Not signed off'
                          : 'Scheduled'}
                    </p>
                    <RowLinkButtons leg={leg} onFlight={onFlight} onLog={onLog} />
                  </div>
                </div>
                {idx < legs.length - 1 && <div className="h-px bg-border-L1 mx-4" />}
              </Fragment>
            ))}
          </Card>
        </Fragment>
      ))}
    </div>
  )
}
